import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toastWarning, toastError, toastSuccess } from "../../utils";
import AppContext from "../../context/appContext";
import { compileOptionsForChapters } from "../../views/AttachmentsScheduler/compileOptions";
import BoxFooter from "../BoxFooter/BoxFooter";
import Button from "../Button/Button";
import ScriptBox from "../ScriptBox/ScriptBox";
import SimpleButton from "../SimpleButton/SimpleButton";
import UploadSection from "../UploadSection/UploadSection";
import UploadService from "../UploadService/UploadService";
import { ReactComponent as UploadIcon } from "../../assets/icons/cloud_upload.svg";
import AttachmentIcon from "../VectorIcons/AttachmentIcon";
import "./style/UploadBox.scss";
import SelectShareButton from "../SelectShareButton/SelectShareButton";
import SelectionActionsBar from "../ModalActionsFooter/SelectionActionsBar";
import {
  uploadMaterialPlanningAttachment,
  uploadChapterPlanningAttachment,
} from "../../services/api/attachments/attachments.api";
import {
  postPlanningEpisodeAddendum,
  postPlanningChapterAddendum,
} from "../../services/api/attachments/addendum.api";
import UserContext from "../../context/userContext";
import { ADDENDUM_TYPE_ID } from "../UploadedFile/UploadInputs/Addendum/addendum.utils";

const UploadBox = ({
  projectId,
  listItems,
  materialsList: materialsListProp = [],
  getPlanning,
  isChapter,
  isMaterial = false,
  isSelectionMode,
  selectedFiles,
  handleSelectOrShare,
  handleCancelSelection,
  handleOpenShareBox,
  handleOpenRelease,
}) => {
  const [files, setFiles] = useState([]);
  const appContext = useContext(AppContext);
  const userContext = useContext(UserContext);

  const haveSharerPermission = userContext.hasPermission({
    required: ["AttachmentSharingWrite"],
  });

  const hasReleasePermission = userContext.hasPermission({
    required: ["AttachmentRelease"],
  });

  const canManageSelection = haveSharerPermission || hasReleasePermission;

  const handlerRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);

  useEffect(() => {
    const node = handlerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsFloating(entry.boundingClientRect.bottom <= 0),
      { threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const hasShownWarningRef = useRef(false);
  const isDramaturgy = appContext.current.step === "dramatologyAttachments";
  const currentType = isMaterial ? "material" : "chapter";

  const warningMessages = {
    material:
      "Atenção, é necessário criar um planejamento antes de subir um arquivo!",
    chapter: "Atenção, é necessário criar um bloco antes de subir um arquivo!",
  };

  const warningMessage = warningMessages[currentType];

  const materialsList = isChapter ? materialsListProp : listItems;
  const dramaturgyList = compileOptionsForChapters(listItems);

  const hasContent = !!listItems?.length || !!materialsList?.length;

  const uploadButtonTypes = {
    default: {
      text: "Upload de arquivos",
      classButton: "buttonBlue large",
      clickEvent: () => appContext.setCurrentStepBox("dramatologyAttachments"),
      children: <UploadIcon />,
      toValidate: true,
      isValidForm: hasContent,
      disabledMessage: warningMessage,
      dataQA: "",
    },
  };

  const showWarningMessage = useCallback(
    () => toastWarning(warningMessage),
    [warningMessage]
  );

  useEffect(() => {
    if (!hasContent && !hasShownWarningRef.current) {
      showWarningMessage();
      hasShownWarningRef.current = true;
    }
  }, [hasContent, showWarningMessage]);

  const handleFilesUpload = (uploadedFiles) => {
    setFiles(uploadedFiles);
  };

  const handleDuplicateFiles = (duplicateFiles) => {
    const message = duplicateFiles
      .flat()
      .map((file) => `${file.originalName} já foi selecionado!`)
      .join("\n");
    toastError(message);
  };

  const uploadFilesIsValid = () => {
    if (!files.length) return false;

    return files.every((file) => {
      if (!file.attachmentTypeId || !file.isSent) return false;

      if (file.attachmentTypeId === ADDENDUM_TYPE_ID) {
        return isChapter
          ? !!file.chapterIds?.length
          : !!file.episodeIds?.length;
      }

      if (!file.version) return false;

      if (file.attachmentTypeId === 1211) {
        if (!file.chapterNumber) return false;
      }

      if (file.structuringReview) {
        return file.reason && file.reason !== "placeholder";
      }

      return true;
    });
  };

  const buildAttachmentPayload = () =>
    files.map((file) => ({
      contentId: projectId,
      fileId: file._meta.tempFileName,
      fileName: file.originalName,
      version: file.version,
      receivedAt:
        appContext.transformDateToUTC(file.receiveAt) || file.receiveAt || null,
      attachmentTypeId: file.attachmentTypeId,
      blockNumber: file?.blockNumber || 1,
      chapterNumber: file.chapterNumber,
      numberChaptersBlock:
        file?.numberChaptersBlock || file?.numberAttachmentsEpisodes,
      rescheduledDelivery: file?.rescheduledDelivery,
      rescheduledRelease: file?.rescheduledRelease,
      scheduleDelivery: file?.scheduleDelivery,
      scheduleRelease: file?.scheduleRelease,
      registerType: 1,
      genresIds: [0],
      spacesIds: [0],
      release: 0,
      contentOrigns: [
        {
          contentOriginId: 0,
          contentOriginDescriptions: ["string"],
        },
      ],
      narratives: [0],
      reason: file.reason,
      structuringReview: file.structuringReview,
      episodeIds: file?.episodeIds || [],
      chapterIds: file?.chapterIds || [],
    }));

  const sendFile = () => {
    appContext.setShowGlassLoading(true);

    const attachments = buildAttachmentPayload();

    const uploadPromises = attachments.map((attachment, index) => {
      const file = files[index];
      const isAddendum = Number(file.attachmentTypeId) === ADDENDUM_TYPE_ID;

      if (isAddendum && isMaterial) {
        return postPlanningEpisodeAddendum({
          attachment,
        });
      }

      if (isAddendum && isChapter) {
        return postPlanningChapterAddendum({
          attachment,
        });
      }

      if (isMaterial) {
        return uploadMaterialPlanningAttachment({
          attachment: attachment,
          materialId: file?.planningId || null,
        });
      }

      if (isChapter) {
        if (file?.blockId) {
          return uploadChapterPlanningAttachment({
            attachment,
            chapterId: file.id || null,
            blockId: file.blockId,
          });
        }
        return uploadMaterialPlanningAttachment({
          attachment: attachment,
          materialId: file?.planningId || null,
        });
      }

      return uploadMaterialPlanningAttachment({
        attachment: attachment,
        materialId: file?.planningId || null,
      });
    });

    Promise.all(uploadPromises)
      .then(() => {
        appContext.setShowGlassLoading(false);
        getPlanning();
        toastSuccess("Arquivo cadastrado com sucesso!");
        appContext.setCurrentStepBox("closed");
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
        toastError("Erro ao cadastrar o arquivo!");
        appContext.setCurrentStepBox("closed");
      });
  };

  const scriptBoxTitle = {
    material: "Novos Anexos de Materiais",
    chapter: "Novos Anexos de Capítulos/Sinopse",
  }[currentType];

  const hasFiles = (() => {
    const materialHasFiles =
      listItems?.some(
        (item) =>
          item.attachments?.length > 0 ||
          item.versions?.some((v) => v.attachments?.length > 0)
      ) ||
      materialsListProp?.some(
        (item) =>
          item.attachments?.length > 0 ||
          item.versions?.some((v) => v.attachments?.length > 0)
      );

    const chapterHasFiles = listItems?.some((block) =>
      block.chapters?.some(
        (chapter) =>
          chapter.attachments?.length > 0 ||
          chapter.addenda?.length > 0 ||
          chapter.versions?.some?.((v) => v.attachments?.length > 0)
      )
    );

    return materialHasFiles || chapterHasFiles;
  })();

  const handlerContent =
    isSelectionMode && canManageSelection ? (
      <SelectionActionsBar
        selectedCount={selectedFiles?.length ?? 0}
        onCancel={handleCancelSelection}
        onShare={handleOpenShareBox}
        onRelease={handleOpenRelease}
        canShare={haveSharerPermission}
        canRelease={hasReleasePermission}
        outlineEnabledButtons
      />
    ) : (
      <>
        <SimpleButton {...uploadButtonTypes.default} />

        {canManageSelection && <div className="divider" />}

        {canManageSelection && (
          <SelectShareButton
            isSelectionMode={isSelectionMode}
            selectedFiles={selectedFiles}
            handleSelectOrShare={handleSelectOrShare}
            isInsideUploadBox={true}
            hasFiles={hasFiles}
          />
        )}
      </>
    );

  return (
    <div id="UploadBox">
      <div className="attachmentsHandler" ref={handlerRef}>
        {handlerContent}
      </div>

      {isFloating && (
        <div className="attachmentsHandler attachmentsHandler--floating">
          {handlerContent}
        </div>
      )}

      {isDramaturgy && (
        <ScriptBox
          open={isDramaturgy}
          title={scriptBoxTitle}
          icon={<AttachmentIcon color="#FFF" />}
          marginBottom={true}
          clear={() => {
            setFiles([]);
            appContext.setCurrentStepBox("closed");
          }}
          size={isChapter ? "width-1100" : "width-940"}
        >
          <div className="scriptBoxUpload">
            <UploadSection
              title={`Anexe aqui os ${
                isChapter ? "capítulos" : "materiais"
              } recebidos`}
            >
              <UploadService
                thesaurusFileType={appContext.currentOriginFileType.result}
                handleOnComplete={handleFilesUpload}
                getDuplicateFiles={handleDuplicateFiles}
                inputName="Dramaturgy"
                dramaturgyList={dramaturgyList}
                materialsList={materialsList}
                isChapter={isChapter}
                isMaterial={isMaterial}
              />
            </UploadSection>
          </div>

          <BoxFooter>
            <Button
              text="Salvar"
              toValidate={true}
              isValidForm={uploadFilesIsValid()}
              classButton="buttonGreen large"
              typeButton="submit"
              dataQA="button-prospect-edit"
              clickEvent={sendFile}
            />
          </BoxFooter>
        </ScriptBox>
      )}
    </div>
  );
};

export default UploadBox;
